import { isFuture, isPast } from 'date-fns';
import { useTranslation } from 'react-i18next';
import useLocale from './useLocale';

interface Event {
  enable_public_resale: boolean;
  public_resale_start_date?: string | null;
  public_resale_end_date?: string | null;
  enable_private_resale: boolean;
  private_resale_start_date?: string | null;
  private_resale_end_date?: string | null;
}

const useResaleState = (event: Event) => {
  const { t } = useTranslation('common');
  const { parseDate, formatDate } = useLocale();

  const hasExpired = (endDate: Date | null) => endDate && !isFuture(endDate);

  const inDateRange = (startDate: Date | null, endDate: Date | null) => (
    (!startDate || isPast(startDate)) && !hasExpired(endDate)
  );

  const getMessage = (enabled: boolean, startDate: Date | null, endDate: Date | null) => {
    if (!enabled) {
      return t('resale.resale_is_disabled');
    }

    const options = { format: 'display_date_time_without_year' } as const;

    if (startDate && !isPast(startDate)) {
      return t('resale.resale_opens_on', { date: formatDate(startDate, options) });
    }

    if (endDate && hasExpired(endDate)) {
      return t('resale.resale_ended_on', { date: formatDate(endDate, options) });
    }

    if (endDate) {
      return t('resale.resale_is_enabled_until', { date: formatDate(endDate, options) });
    }

    return t('resale.resale_is_enabled');
  };

  const calculate = (enabled: boolean, start: string | null, end: string | null) => {
    const options = { format: 'internal_date_time', timezone: 'UTC' } as const;
    const startDate = start ? parseDate(start, options) : null;
    const endDate = end ? parseDate(end, options) : null;

    return {
      enabled: enabled && inDateRange(startDate, endDate),
      message: getMessage(enabled, startDate, endDate),
    };
  };

  return {
    publicResale: calculate(
      event.enable_public_resale,
      event.public_resale_start_date,
      event.public_resale_end_date,
    ),
    privateResale: calculate(
      event.enable_private_resale,
      event.private_resale_start_date,
      event.private_resale_end_date,
    ),
  };
};

export default useResaleState;
